import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FloorPlanSvg, FloorLegend } from '../components/FloorPlanSvg';
import { BILLBOARD_FONT, formatBillboardDate } from '../components/BillboardTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const W = 1080;
const H = 1920;
// Legend may use y 120..1010; MAIN AREA tables start at y≈1030.
const LEGEND_MAX_H = 880;

const computeTodayKst = () => {
  const now = new Date();
  const kst = new Date(now.getTime() + (9 * 60 - now.getTimezoneOffset()) * 60 * 1000);
  return `${kst.getFullYear()}-${String(kst.getMonth() + 1).padStart(2, '0')}-${String(kst.getDate()).padStart(2, '0')}`;
};

// Floor-plan billboard, portrait 1080x1920. The plan's coordinate space equals
// the board, so tables sit exactly where the layout puts them on the artwork.
// ?date= pins a day, otherwise it follows today (KST) and flips at midnight.
export default function FloorPlanView() {
  const [params] = useSearchParams();
  const urlDate = params.get('date');
  const bgImg = params.get('bg') || '';
  const [autoDate, setAutoDate] = useState(computeTodayKst());
  const date = urlDate || autoDate;
  const [data, setData] = useState(null);
  const legendRef = useRef(null);
  const [legendScale, setLegendScale] = useState(1);

  useLayoutEffect(() => {
    const el = legendRef.current;
    if (!el) return undefined;
    const fit = () => {
      // measure unscaled content height
      const natural = el.scrollHeight;
      setLegendScale(natural > LEGEND_MAX_H ? Math.max(0.5, (LEGEND_MAX_H / natural) * 0.98) : 1);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    if (urlDate) return undefined;
    const id = setInterval(() => {
      const t = computeTodayKst();
      setAutoDate(prev => (prev !== t ? t : prev));
    }, 30 * 1000);
    return () => clearInterval(id);
  }, [urlDate]);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/floor/live`, { params: { date }, timeout: 60000 });
      setData(r.data);
    } catch (e) {
      console.warn('floor fetch failed, retrying', e.message);
      setTimeout(fetchData, 10000);
    }
  }, [date]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 15000);
    return () => clearInterval(id);
  }, [fetchData]);

  useEffect(() => {
    const t = setTimeout(() => window.location.reload(), 60 * 60 * 1000);
    return () => clearTimeout(t);
  }, []);

  if (!data) return <div style={{ background: '#080404', width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: BILLBOARD_FONT }}>Loading...</div>;

  const events = data.events || [];
  const bg = bgImg || data.bg_url || '';
  // Always the large legend; busy days (>10 events) use one-line names so the
  // text stays big, and items are spread evenly down the available space.
  const large = true;
  const dense = events.length > 10;


  return (
    <div data-testid="floor-billboard" style={{
      width: W, height: H, margin: '0 auto', position: 'relative', overflow: 'hidden',
      background: '#080404',
      backgroundImage: bg ? `url("${bg}")` : 'none', backgroundSize: 'cover', backgroundPosition: 'center',
      fontFamily: BILLBOARD_FONT, WebkitFontSmoothing: 'antialiased',
    }}>
      {/* One soft dark layer over the whole artwork so everything stays readable */}
      {bg && <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,4,4,0.6)' }} data-testid="floor-overlay" />}

      {/* Floor plan drawn over the whole board */}
      <FloorPlanSvg data={data} showSeats={false} style={{ position: 'absolute', inset: 0, width: W, height: H, display: 'block' }} preserveAspectRatio="xMidYMin meet" />

      {/* Heading on top */}
      <div style={{ position: 'absolute', top: 22, left: 0, right: 0, textAlign: 'center', textShadow: '0 2px 8px rgba(0,0,0,0.95)' }}>
        <div style={{ fontSize: 34, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff' }} data-testid="floor-heading">Daily Tournament Schedule</div>
        <div style={{ fontSize: 28, fontWeight: 600, color: '#facc15', textTransform: 'uppercase', marginTop: 2 }} data-testid="floor-date">{formatBillboardDate(date)}</div>
      </div>

      {/* Legend, top-left — auto-scales down so it never reaches the tables below */}
      <div style={{ position: 'absolute', top: 120, left: 40, width: 600, height: LEGEND_MAX_H, overflow: 'hidden' }}>
        <div ref={legendRef} style={{ transform: `scale(${legendScale})`, transformOrigin: 'top left', width: 600, height: legendScale === 1 ? LEGEND_MAX_H : 'auto' }}>
          {events.length === 0
            ? <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 24, textShadow: '0 2px 6px rgba(0,0,0,0.9)' }}>No tournaments scheduled</div>
            : <FloorLegend events={events} large={large} dense={dense} fillHeight={legendScale === 1} arrowStyle={data.arrow_style} showCounts={false} />}
        </div>
      </div>

      {!data.pokerlens_ok && (
        <div style={{ position: 'absolute', right: 16, bottom: 8, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>live seats unavailable</div>
      )}
    </div>
  );
}
